import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { UpdatePayrollDTO } from "@/modules/hr/payroll/domain/types";
import {
  computeTotals,
  formValuesToItems,
  PayrollEntryForm,
  PayrollEntryHeader,
} from "../ui/PayrollEntryForm";
import { usePayrollDetailQuery } from "../api/queries";
import { usePayrollStatusMutation, usePayrollUpdateMutation } from "../api/queries";
import type { PayrollFormValues } from "../ui/PayrollEntryForm";
import { PayrollFormTour, usePayrollFormTour } from "../tour/PayrollFormTour";

const MONTH_NAMES = [
  "",
  "ມ.ກ.",
  "ກ.ພ.",
  "ມ.ນ.",
  "ມ.ສ.",
  "ພ.ສ.",
  "ມ.ຖ.",
  "ກ.ກ.",
  "ສ.ຫ.",
  "ກ.ຍ.",
  "ຕ.ລ.",
  "ພ.ຈ.",
  "ທ.ວ.",
];

export function PayrollFormPage() {
  const { id } = useParams({ from: "/app/hr/payroll/$id/edit" });
  const nav = useNavigate();
  const detailQuery = usePayrollDetailQuery(id);
  const updateMutation = usePayrollUpdateMutation();
  const statusMutation = usePayrollStatusMutation();
  const tour = usePayrollFormTour();
  const payroll = detailQuery.data;

  const isSubmitting = updateMutation.isPending || statusMutation.isPending;

  const handleCancel = () => {
    nav({
      to: "/app/hr/payroll",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        filters: undefined,
        periodYear: undefined,
        periodMonth: undefined,
      },
    });
  };

  const buildUpdateDto = (values: PayrollFormValues): UpdatePayrollDTO => {
    const totals = computeTotals(values);
    const items = formValuesToItems(values);
    const ot = Number(String(values.otAmount).replace(/,/g, "")) || 0;
    const bonus = Number(String(values.bonusAmount).replace(/,/g, "")) || 0;
    return {
      baseSalary: totals.baseSalary,
      otAmount: ot,
      bonusAmount: bonus,
      totalEarnings: totals.totalEarnings,
      totalDeductions: totals.totalDeductions,
      netSalary: totals.netSalary,
      notes: values.notes.trim() || null,
      items,
    };
  };

  const handleSaveDraft = (values: PayrollFormValues) => {
    if (!id) return;
    updateMutation.mutate(
      { id, dto: buildUpdateDto(values) },
      {
        onSuccess: () => {
          detailQuery.refetch();
        },
      },
    );
  };

  const handleSaveAndConfirm = (values: PayrollFormValues) => {
    if (!id) return;
    const dto = buildUpdateDto(values);
    updateMutation.mutate(
      { id, dto },
      {
        onSuccess: () => {
          statusMutation.mutate(
            { id, status: "confirmed" },
            {
              onSuccess: () => {
                detailQuery.refetch();
              },
            },
          );
        },
      },
    );
  };

  if (detailQuery.isLoading || !id) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (detailQuery.isError || !payroll) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-destructive">ບໍ່ພົບຂໍ້ມູນ ຫຼື ເກີດຂໍ້ຜິດພາດ</p>
          <button
            type="button"
            className="text-primary underline"
            onClick={() =>
              nav({
                to: "/app/hr/payroll",
                search: {
                  offset: 0,
                  limit: 20,
                  sort: undefined,
                  filters: undefined,
                  periodYear: undefined,
                  periodMonth: undefined,
                },
              })
            }
          >
            ກັບຄືນ
          </button>
        </Main>
      </>
    );
  }

  const periodLabel = MONTH_NAMES[payroll.periodMonth]
    ? `${MONTH_NAMES[payroll.periodMonth]} ${payroll.periodYear}`
    : `${payroll.periodYear}/${payroll.periodMonth}`;
  const mode = payroll.status === "paid" ? "view" : "edit";

  const handlePreview = () => {
    nav({
      to: "/app/hr/payroll/$id/slip",
      params: { id: payroll.id },
      search: { print: undefined },
    });
  };

  const handlePrint = () => {
    nav({
      to: "/app/hr/payroll/$id/slip",
      params: { id: payroll.id },
      search: { print: 1 },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <PayrollFormTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div data-tourid="payroll-form-back">
          <PayrollEntryHeader
            status={payroll.status}
            periodLabel={periodLabel}
            employeeName={payroll.employeeName}
            title="ເອກະສານເງິນເດືອນພະນັກງານ"
            subtitle="ກວດສອບ ແລະ ປັບແຕ່ງການຈ່າຍເງິນສໍາລັບໄລຍະນີ້"
            onBack={handleCancel}
            onPreview={handlePreview}
            onPrint={handlePrint}
            onStartTour={tour.startTour}
          />
        </div>
        <div data-tourid="payroll-form-form">
          <PayrollEntryForm
          mode={mode}
          payroll={payroll}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          onSaveAndConfirm={handleSaveAndConfirm}
          isSubmitting={isSubmitting}
        />
        </div>
      </Main>
    </>
  );
}
